import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import {
  loadCategoryToShort,
  getLoadCaseLabel,
  naturalSortComparator,
  combinePointLoadsByLoadCaseAndPosition,
} from '@editorUtils'
import { filter, isEqual, reject, uniq } from 'lodash-es'
import numeral from 'numeral'
import { VisibilityOff, Visibility } from '@mui/icons-material'
import { IconButton, Typography, Stack, Box } from '@mui/material'
import {
  DataGridPremium,
  GridColDef,
  GridFilterInitialState,
  GridLogicOperator,
  GridRenderCellParams,
  GridState,
} from '@mui/x-data-grid-premium'
import useLoadsCombinedStore from '@editor/pages/Results2/stores/useLoadsCombinedStore'
import { FocusIconButton } from '@ui/actions'
import { useControlStore, useModelStore } from '@editorStores'
import { useElementLabel } from '@editorHooks'
import LoadSourceLabel from '../LoadSourceLabel'
import LoadTraceIconButton from '../LoadTraceIconButton'
import LoadCustomToolbar from '../LoadsToolBar/CustomToolbar'
import { pointLoadForceValueGetter, pointLoadForceFormatter } from './utils'

interface Props {
  pointLoads: PointLoadWithDomain[]
  deadLoadFilterByDefault?: boolean
  setHiddenLoads: (loads: string[]) => void
  hiddenLoads: string[]
  selectedLoad: string | null
  onSelectLoad: (load: string | null) => void
  onSelectSource: (guid: string) => void
  tracedLoads?: string[] | null
  onTraceLoads?: (loadGuids: string[] | null) => void
}

const PointLoadTable = ({
  pointLoads,
  deadLoadFilterByDefault,
  hiddenLoads,
  setHiddenLoads,
  selectedLoad,
  onSelectLoad,
  onSelectSource,
  tracedLoads,
  onTraceLoads,
}: Props): ReactElement => {
  // Access results store directly
  const isDrawerExpanded = useControlStore(state => state.isDrawerExpanded)
  const getLabel = useElementLabel()

  const loadsCombined = useLoadsCombinedStore(state => state.loadsCombined)
  const setLoadsCombined = useLoadsCombinedStore(state => state.setLoadsCombined)
  const focusElements = useModelStore(state => state.focusElements)

  const pointLoadsCombined = useMemo(
    () => (loadsCombined ? combinePointLoadsByLoadCaseAndPosition(pointLoads) : pointLoads),
    [loadsCombined, pointLoads],
  )

  const pointLoadGuids = useMemo(
    () => pointLoadsCombined.map(load => load.guid),
    [pointLoadsCombined],
  )

  const defaultFilteredLoads = useMemo(() => {
    if (!deadLoadFilterByDefault) return []

    return reject(pointLoadsCombined, load => load.load_case.category === 'Dead').map(
      load => load.guid,
    )
  }, [deadLoadFilterByDefault, pointLoadsCombined])

  const [localHiddenLoads, setLocalHiddenLoads] = useState<string[]>([])
  const [filteredLoads, setFilteredLoads] = useState<string[]>(defaultFilteredLoads)

  const allLoadsAreHidden = useMemo(() => {
    return pointLoadGuids.every(guid => hiddenLoads.includes(guid))
  }, [pointLoadGuids, hiddenLoads])

  const setAllLoadsVisible = useCallback(() => {
    // only set the given lineLoads to visible
    setLocalHiddenLoads(reject(localHiddenLoads, guid => pointLoadGuids.includes(guid)))
  }, [localHiddenLoads, pointLoadGuids])

  const setAllLoadsHidden = useCallback(() => {
    // Combine existing hidden loads with the line load guids, removing duplicates
    setLocalHiddenLoads([...new Set([...localHiddenLoads, ...pointLoadGuids])])
  }, [localHiddenLoads, pointLoadGuids])

  const handleVisibilityColumnHeaderClick = useCallback(() => {
    if (allLoadsAreHidden) {
      setAllLoadsVisible()
    } else {
      setAllLoadsHidden()
    }
  }, [allLoadsAreHidden, setAllLoadsHidden, setAllLoadsVisible])

  const toggleHideLoad = useCallback(
    (loadGuid: string) => {
      if (localHiddenLoads.includes(loadGuid)) {
        setLocalHiddenLoads(reject(localHiddenLoads, hiddenLoad => hiddenLoad === loadGuid))
      } else {
        setLocalHiddenLoads([...localHiddenLoads, loadGuid])
      }
    },
    [localHiddenLoads],
  )

  useEffect(() => {
    const allHiddenLoads = uniq([...localHiddenLoads, ...filteredLoads])
    setHiddenLoads(allHiddenLoads)

    return () => setHiddenLoads([])
  }, [filteredLoads, localHiddenLoads, setHiddenLoads])

  const rows = useMemo(() => {
    return pointLoadsCombined.map(load => {
      // Check if this is a combined load with original load guids
      const originalGuids = (load as PointLoadWithDomain & { originalLoadGuids?: string[] })
        .originalLoadGuids

      let sourceGuids: string[] = []
      let hasExternLoad = false

      if (originalGuids && originalGuids.length > 0) {
        sourceGuids = originalGuids
          .map(
            originalGuid => pointLoads.find(load => load.guid === originalGuid)?.user_shown_source,
          )
          .filter((guid): guid is string => !!guid)
        hasExternLoad = originalGuids.length > sourceGuids.length
      } else {
        if (load.user_shown_source) {
          sourceGuids = [load.user_shown_source]
        } else {
          hasExternLoad = true
        }
      }

      // Add "extern" marker if there are loads without sources
      if (hasExternLoad) {
        sourceGuids.push('extern')
      }

      // Remove duplicates
      sourceGuids = Array.from(new Set(sourceGuids))

      // Raw load guids represented by this row (for combined rows all original loads)
      const traceGuids = originalGuids && originalGuids.length > 0 ? originalGuids : [load.guid]

      return {
        id: load.guid,
        ...load,
        position: load.domain?.length * load.relative_position,
        domainLength: load.domain?.length,
        originalLoadGuids: originalGuids,
        traceGuids,
        sourceGuids,
        sourceLabel: sourceGuids.length > 0 ? sourceGuids.map(s => getLabel(s)).join(', ') : '',
        visible: !hiddenLoads.includes(load.guid),
        // fields for filtering
        loadCaseType: load.load_case.load_case_type,
        loadCaseCategory: load.load_case.category,
        loadCaseTypeLabelShort: getLoadCaseLabel(load.load_case)?.short,
        loadCaseTypeLabel: getLoadCaseLabel(load.load_case)?.label,
        loadCaseCategoryShort: loadCategoryToShort[load.load_case.category],
      }
    })
  }, [pointLoadsCombined, pointLoads, hiddenLoads, getLabel])

  const rowSelectionModel = useMemo(() => {
    if (!selectedLoad) return []

    const selectedRow = rows.find(
      row =>
        row.guid === selectedLoad ||
        (row.originalLoadGuids as string[] | undefined)?.includes(selectedLoad),
    )

    return selectedRow ? [selectedRow.guid] : []
  }, [selectedLoad, rows])

  const defaultColumnProps = {
    renderHeaderFilter: () => null,
  }

  const columnWidths = isDrawerExpanded
    ? {
        visibility: 30,
        force: 100,
        position: 75,
        loadCategoryType: 50,
        loadCaseType: 50,
        source: 170,
        startPosition: 65,
        endPosition: 65,
      }
    : {
        visibility: 30,
        force: 75,
        position: 75,
        loadCategoryType: 35,
        loadCaseType: 50,
        source: 60,
      }

  const columns: GridColDef[] = [
    {
      ...defaultColumnProps,
      field: 'visible',
      headerName: 'Sichtbarkeit',
      width: columnWidths.visibility,
      minWidth: columnWidths.visibility,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      disableColumnMenu: true,
      type: 'boolean',
      renderHeader: () => {
        return (
          <IconButton onClick={handleVisibilityColumnHeaderClick} size="small">
            {allLoadsAreHidden ? (
              <VisibilityOff fontSize="inherit" />
            ) : (
              <Visibility fontSize="inherit" />
            )}
          </IconButton>
        )
      },
      renderCell: (params: GridRenderCellParams) => (
        <IconButton onClick={() => toggleHideLoad(params.row.guid)} size="small">
          {params.row.visible ? (
            <Visibility fontSize="inherit" />
          ) : (
            <VisibilityOff fontSize="inherit" />
          )}
        </IconButton>
      ),
    },
    {
      field: 'loadCaseType',
      headerName: 'Fall',
      width: columnWidths.loadCaseType,
      minWidth: columnWidths.loadCaseType,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      valueGetter: (_, row: PointLoadWithDomain) => getLoadCaseLabel(row.load_case)?.short,
      sortComparator: naturalSortComparator,
    },
    {
      ...defaultColumnProps,
      field: 'loadCaseTypeLabelShort',
      headerName: 'FallKurzbez.',
      width: columnWidths.loadCaseType,
      minWidth: columnWidths.loadCaseType,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      hideable: true,
    },
    {
      field: 'force',
      headerName: 'F',
      width: columnWidths.force,
      sortable: true,
      headerAlign: 'center',
      align: 'right',
      renderHeader: () => {
        return (
          <Stack direction="column" spacing={0} padding={0} alignItems="center">
            <Typography>F</Typography>
            <Typography>[kN]</Typography>
          </Stack>
        )
      },
      valueGetter: (_, row: PointLoadWithDomain) =>
        pointLoadForceValueGetter(row.force, row.domain as Domain),
      valueFormatter: pointLoadForceFormatter,
      type: 'number',
    },
    {
      ...defaultColumnProps,
      field: 'position',
      headerName: 'Pos.',
      width: columnWidths.position,
      headerAlign: 'center',
      align: 'right',
      sortable: true,
      valueFormatter: (value: number) => `${numeral(value).format('0.00')} m`,
    },
    {
      field: 'sourceLabel',
      headerName: 'Quelle',
      width: columnWidths.source,
      minWidth: columnWidths.source,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <LoadSourceLabel sources={params.row.sourceGuids} onSelectSource={onSelectSource} />
      ),
    },

    {
      field: 'loadCaseCategoryShort',
      headerName: 'Kat.',
      width: columnWidths.loadCategoryType,
      minWidth: columnWidths.loadCategoryType,
      headerAlign: 'center',
      align: 'center',
      sortable: true,
    },
    ...(loadsCombined || onTraceLoads
      ? [
          {
            ...defaultColumnProps,
            field: 'actions',
            headerName: 'Aktionen',
            type: 'actions',
            width: loadsCombined && onTraceLoads ? 70 : 50,
            minWidth: loadsCombined && onTraceLoads ? 70 : 50,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => {
              // Filter out 'extern' from sourceGuids
              const validSourceGuids = (params.row.sourceGuids as string[]).filter(
                guid => guid !== 'extern',
              )
              const traceGuids = params.row.traceGuids as string[]
              const isTraced =
                !!tracedLoads && isEqual([...tracedLoads].sort(), [...traceGuids].sort())
              const showFocus = loadsCombined && validSourceGuids.length > 0
              const showTrace = !!onTraceLoads
              if (!showFocus && !showTrace) return null

              return (
                <>
                  {showFocus && (
                    <FocusIconButton
                      hint="Lastpfad fokussieren"
                      onClick={e => {
                        e.stopPropagation()
                        focusElements([...validSourceGuids, params.row.element_guid])
                      }}
                    />
                  )}
                  {showTrace && (
                    <LoadTraceIconButton
                      active={isTraced}
                      onClick={e => {
                        e.stopPropagation()
                        if (isTraced) {
                          onTraceLoads?.(null)
                        } else {
                          onSelectLoad(params.row.guid)
                          onTraceLoads?.(traceGuids)
                        }
                      }}
                    />
                  )}
                </>
              )
            },
          } as GridColDef,
        ]
      : []),
  ]

  const isDrawerExpandedExtraProps = isDrawerExpanded
    ? {
        headerFilters: true,
        headerFilterHeight: 75,
      }
    : {}

  const slots = isDrawerExpanded
    ? {
        toolbar: LoadCustomToolbar,
      }
    : {
        toolbar: LoadCustomToolbar,
      }

  const gridFilterInitialState: GridFilterInitialState | undefined = deadLoadFilterByDefault
    ? {
        filterModel: {
          items: [
            {
              field: 'loadCaseType',
              operator: 'equals',
              value: 'g',
            },
            {
              field: 'force',
              operator: '<',
            },
          ],
          quickFilterExcludeHiddenColumns: false,
          quickFilterLogicOperator: GridLogicOperator.Or,
        },
      }
    : {
        filterModel: {
          items: [
            {
              field: 'force',
              operator: '<',
            },
          ],
          quickFilterExcludeHiddenColumns: true,
          quickFilterLogicOperator: GridLogicOperator.Or,
        },
      }

  const handleStateChange = useCallback(
    (state: GridState) => {
      const filteredRows = filter(
        Object.entries(state.filter.filteredRowsLookup),
        ([, visible]) => !visible,
      )

      const filteredKeys = filteredRows.map(visibleRow => visibleRow[0])

      if (!isEqual(filteredKeys, filteredLoads)) {
        setFilteredLoads(filteredKeys)
      }
    },
    [filteredLoads],
  )

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <DataGridPremium
        rows={rows}
        columns={columns}
        columnHeaderHeight={75}
        density="compact"
        hideFooter
        onRowClick={params => {
          onSelectLoad(params.row.guid)
        }}
        rowSelectionModel={rowSelectionModel}
        initialState={{
          sorting: {
            sortModel: [{ field: 'loadCaseType', sort: 'asc' }],
          },
          pagination: {
            paginationModel: { pageSize: 25 },
          },
          columns: {
            columnVisibilityModel: {
              loadCaseTypeLabelShort: false,
            },
          },
          filter: gridFilterInitialState,
        }}
        autoPageSize
        getRowId={row => row.id}
        disableRowSelectionOnClick
        disableRowGrouping
        // @ts-expect-error
        slots={slots}
        slotProps={{
          toolbar: {
            // @ts-expect-error
            loadsCombined: loadsCombined,
            onLoadsCombinedChange: setLoadsCombined,
          },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            padding: 0,
          },
          '& .MuiDataGrid-columnHeader': {
            padding: 0,
          },
        }}
        onStateChange={handleStateChange}
        {...isDrawerExpandedExtraProps}
        data-cy="point-load-table"
      />
    </Box>
  )
}

export default PointLoadTable
